import type { LocationQueryValue } from 'vue-router'
import { Buffer } from 'buffer'
import dayjs from 'dayjs'
import axios from 'axios'

import notification from '@shared/components/ui/c-notification'

import { lowerCaseReg, numberReg, specialCharReg, upperCaseReg, uuidReg } from '@shared/utils/regexp'
import { getDocumentName, OBSERVATION_ITEM, PAGINATION_STEP } from '@shared/types/constants'
import type IPatient from '@shared/api/patients/resources/patients/patient'
import type { EForeignSystem } from '@shared/api/patients/resources/patients/constants/foreign-system'
import type IVisitDocument from '@shared/api/visits/resources/documents/visit-document'
import type IChartObservation from '@shared/api/patients/resources/chart/observation'
import type IFilteredChartData from '@shared/types/medical-data/observations/filtered-chart-data'
import { EPatientType } from '@shared/types/constants/resident-type'
import { EChartObservationType } from '@shared/api/patients/resources/chart/constants/observation-type'
import { formatDate } from '@shared/utils/date-format'
import { EDateFormat } from '@shared/types/constants/date-format'
import { EDocumentType } from '@shared/api/visits/resources/documents/constants/document-type'
import { EUserRole } from '@shared/api/user-management/resources/user/constants/user-roles'

export const isLastArrayEl = (index: string | number, array: any[]): boolean => index === array.length - 1

export const capitalize = (text: string) => (text ? text[0].toUpperCase() + text.slice(1).toLowerCase() : '-')

export const rmSymbolsForPhone = (number = '') => {
  return number?.length ? `+${number.replace(/[^\d]/g, '')}` : number
}

export const normalizeText = (text: string) => {
  return text
    .split('_')
    .map(word => word[0] + word.slice(1).toLowerCase())
    .join(' ')
}

export const formatReason = (reason = ''): string => {
  const formattedReason = reason.replaceAll('_', ' ').toLowerCase()
  return formattedReason.charAt(0).toUpperCase() + formattedReason.slice(1)
}

export const conversionString = (string = '', joinSymbol = ' ') => {
  if (!string) {
    return ''
  }
  return string.match(/.{1,3}/g)?.join(joinSymbol)
}

export const calcAspectRatio = (width: number, height: number): number => {
  return width > height ? width / height : height / width
}

export const isUUID = (id: string) => {
  return uuidReg.test(id)
}

export const hasLowerCase = (str: string) => {
  return lowerCaseReg.test(str)
}

export const hasUpperCase = (str: string): boolean => {
  return upperCaseReg.test(str)
}

export const hasNumber = (str: string): boolean => {
  return numberReg.test(str)
}

export const hasMinLength = (str: string, length = 8) => {
  return str && str.length >= length
}

export const hasSpecialChar = (str: string): boolean => {
  return specialCharReg.test(str)
}

export const fetchAbbrString = (firstName?: string, lastName?: string): string => {
  if (firstName && lastName) {
    const name = firstName[0] + lastName[0]
    return name ? name.toUpperCase() : ''
  }
  if (firstName && firstName.length) {
    const splittedName = firstName.split(' ').filter(Boolean)
    const name = splittedName.length > 1 ? splittedName[0][0] + splittedName[1][0] : firstName[0] + (firstName[1] || '')
    return name ? name.toUpperCase() : ''
  }
  if (lastName) {
    const name = lastName[0] + (lastName[1] || '')
    return name ? name.toUpperCase() : ''
  }
  return ''
}

export const parseJwt = (token: string | null) => {
  if (!token) {
    return
  }
  return JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString())
}

export const copyToClipboard = (text = '') => {
  if (!text) {
    return
  }
  navigator.clipboard.writeText(text)
  notification.open({
    message: 'Copied to clipboard',
  })
}

export declare type LocationQuery = Record<string, LocationQueryValue | LocationQueryValue[]>
export const joinCommunityIds = (communityIds: LocationQueryValue | LocationQueryValue[]) => {
  if (Array.isArray(communityIds)) {
    return `?communityIds=${communityIds?.join('&communityIds=')}`
  } else if (communityIds?.length) {
    return `?communityIds=${communityIds}`
  }
  return ''
}

export const getPatientForeignId = (system: EForeignSystem, patient?: IPatient | null) => {
  const foreignId = patient?.foreignIds?.find(el => el.foreignSystem === system)
  return foreignId?.patientForeignId || ''
}

export const getFilteredDocumentsList = (documentsList: IVisitDocument[]): IVisitDocument[] => {
  const reducedDocumentList: Record<string, IVisitDocument[]> = documentsList.reduce(
    (acc: Record<string, IVisitDocument[]>, item: IVisitDocument) => {
      const { documentType } = item
      if (!acc[documentType]) {
        acc[documentType] = []
      }
      const documentListLength = acc[documentType].length
      const count: number | string = documentListLength ? documentListLength + 1 : ''
      const name =
        documentType === EDocumentType.SNAPSHOT ? item.description : getDocumentName(item.documentType, count)
      acc[documentType].push({
        ...item,
        name,
      })
      return acc
    },
    {}
  )
  return Object.values(reducedDocumentList).flat(1)
}

export const getPatientType = (type: string | EPatientType | EPatientType[] | null | undefined) => {
  switch (type) {
    case EPatientType.AL:
      return 'AL'
    case EPatientType.IL:
      return 'IL'
    case EPatientType.MC:
      return 'MC'
    case EPatientType.UNKNOWN:
      return 'Unknown'
    case EPatientType.OTHER:
      return 'Other'
    case EPatientType.EMPLOYEE:
      return 'Employee'
    default:
      return 'Unknown'
  }
}

export const rmAllSymbols = (field: string) => {
  return field.replace(/[^\d]/g, '')
}

export const fetchFullName = ({ firstName, lastName }: { firstName: string; lastName: string }) => {
  if (firstName && lastName) {
    return `${firstName} ${lastName}`
  }
  if (firstName) {
    return firstName
  }
  if (lastName) {
    return lastName
  }
  return ''
}

export const fetchChartObservationData = ({
  chartList,
  type,
  currentObservationCount,
}: {
  chartList: IChartObservation[]
  type: string
  currentObservationCount: number
}) => {
  const data: IFilteredChartData = {
    chartData: {
      datasetLabel: OBSERVATION_ITEM[type],
      diastolicLabel: 'Diastolic Value',
      systolicLabel: 'Systolic Value',
      diastolicValue: [],
      systolicValue: [],
      value: [],
      valueUnit: '',
    },
    tableData: [],
  }

  data.tableData = [...chartList].sort((a, b) => {
    return dayjs(a.effectiveDate).valueOf() - dayjs(b.effectiveDate).valueOf()
  })
  const chartLength = data.tableData.length
  const isBloodPressureData = type === EChartObservationType.BLOOD_PRESSURE
  const isShowNavButtons = [...data.tableData].length > PAGINATION_STEP
  const chartData = isShowNavButtons
    ? [...data.tableData].splice(currentObservationCount, PAGINATION_STEP)
    : [...data.tableData]
  chartData.forEach(i => {
    data.chartData.valueUnit = i.valueUnit
    if (isBloodPressureData) {
      data.chartData.diastolicValue.push({
        x: formatDate({ date: i.effectiveDate, format: EDateFormat.FULL_DATE_TIME, isByTimeZone: false }),
        y: i.valueBPDiastolic,
      })
      data.chartData.systolicValue.push({
        x: formatDate({ date: i.effectiveDate, format: EDateFormat.FULL_DATE_TIME, isByTimeZone: false }),
        y: i.valueBPSystolic,
      })
    } else {
      data.chartData.value.push({
        x: formatDate({ date: i.effectiveDate, format: EDateFormat.FULL_DATE_TIME, isByTimeZone: false }),
        y: i.value,
      })
    }
  })

  return { data, chartLength, isBloodPressureData, isShowNavButtons }
}

export const fieldFormatter = (value: string) => {
  return value.replace(/^\s+/, '').replace(/\s{2,}/, ' ')
}

export const appHeight = () => {
  document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`)
}

export const createImageSrcFromVideo = ({ elementId }: { elementId: string }): string => {
  const videoElement = document.getElementById(elementId) as HTMLVideoElement
  const canvas = document.createElement('canvas')
  canvas.width = videoElement.videoWidth
  canvas.height = videoElement.videoHeight
  canvas.id = new Date().getTime().toString()
  const context = canvas.getContext('2d')
  context?.drawImage(videoElement, 0, 0, canvas.width, canvas.height)
  return canvas.toDataURL('image/png')
}

export const formatRoleName = (role: EUserRole, name?: string): string => {
  switch (role) {
    case EUserRole.COMMUNITY_ADMIN:
      return 'Location admin'
    case EUserRole.COMMUNITY_RESIDENT_MANAGEMENT:
      return 'Location patient management'
    case EUserRole.MEMBER:
      return 'Member'
    default:
      return name || '-'
  }
}

export const checkForLatestVersion = async () => {
  const { data } = await axios.get('/version.json')
  const storedVersion = localStorage.getItem('version')
  if (!storedVersion && data?.version) {
    localStorage.setItem('version', data.version)
    return true
  }
  if (storedVersion !== data?.version) {
    localStorage.setItem('version', data.version)
    location.reload()
  }
}
